import { useState } from 'react';
import { useParams, Link, useLocation } from 'wouter';
import { AppShell } from '@/components/AppShell';
import {
  useGetSite,
  useGetWidgetConfig,
  useUpdateWidgetConfig,
  useListScans,
  useRunScan,
  useVerifySiteInstall,
  useDeleteSite,
  useRotateSiteToken,
  getGetSiteQueryKey,
  getGetWidgetConfigQueryKey,
  getListScansQueryKey,
  getListSitesQueryKey,
} from '@workspace/api-client-react';
import {
  ArrowLeft,
  Copy,
  CheckCircle2,
  AlertCircle,
  Clock,
  Settings as SettingsIcon,
  FileSearch,
  Play,
  Trash2,
  Download,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useQueryClient } from '@tanstack/react-query';
import { useToast } from '@/hooks/use-toast';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { formatDistanceToNow } from 'date-fns';
import { InstallationWizard } from '@/components/InstallationWizard';

const ALL_FEATURES = [
  { id: 'textSize', label: 'Text Size Adjustment' },
  { id: 'contrast', label: 'High Contrast' },
  { id: 'darkMode', label: 'Dark Mode' },
  { id: 'saturation', label: 'Saturation Control' },
  { id: 'dyslexiaFont', label: 'Dyslexia-Friendly Font' },
  { id: 'lineHeight', label: 'Line Height' },
  { id: 'underlineLinks', label: 'Underline Links' },
  { id: 'hideImages', label: 'Hide Images' },
  { id: 'readingGuide', label: 'Reading Guide' },
  { id: 'readingMask', label: 'Reading Mask' },
  { id: 'highlightLinks', label: 'Highlight Links' },
  { id: 'focusHighlight', label: 'Focus Highlight' },
  { id: 'largeCursor', label: 'Large Cursor' },
  { id: 'pauseAnimations', label: 'Pause Animations' },
  { id: 'profiles', label: 'Accessibility Profiles' },
];

export default function SiteDetailPage() {
  const params = useParams();
  const siteId = params.siteId ? Number(params.siteId) : null;
  const [, setLocation] = useLocation();
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const { data: site, isLoading: siteLoading } = useGetSite(siteId!, {
    query: { enabled: !!siteId, queryKey: getGetSiteQueryKey(siteId!) },
  });
  const { data: config, isLoading: configLoading } = useGetWidgetConfig(siteId!, {
    query: { enabled: !!siteId, queryKey: getGetWidgetConfigQueryKey(siteId!) },
  });
  const { data: scans, isLoading: scansLoading } = useListScans(siteId!, {
    query: { enabled: !!siteId, queryKey: getListScansQueryKey(siteId!) },
  });

  const updateConfig = useUpdateWidgetConfig();
  const runScan = useRunScan();
  const verifySite = useVerifySiteInstall();
  const deleteSite = useDeleteSite();
  const rotateToken = useRotateSiteToken();

  const [localConfig, setLocalConfig] = useState<any>(null);

  const currentConfig = localConfig || config;

  const handleConfigChange = (field: string, value: any) => {
    setLocalConfig((prev: any) => ({ ...(prev || config), [field]: value }));
  };

  const handleSaveConfig = () => {
    if (!siteId || !currentConfig) return;
    const { siteId: _, ...updateData } = currentConfig;
    updateConfig.mutate(
      { siteId, data: updateData },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: getGetWidgetConfigQueryKey(siteId) });
          toast({ title: 'Configuration saved' });
          setLocalConfig(null);
        },
        onError: () => {
          toast({ title: 'Failed to save configuration', variant: 'destructive' });
        },
      }
    );
  };

  const handleRunScan = () => {
    if (!siteId) return;
    runScan.mutate(
      { siteId },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: getListScansQueryKey(siteId) });
          toast({ title: 'Scan started' });
        },
        onError: () => {
          toast({ title: 'Failed to start scan', variant: 'destructive' });
        },
      }
    );
  };

  const handleVerify = () => {
    if (!siteId) return;
    verifySite.mutate(
      { siteId },
      {
        onSuccess: (result) => {
          queryClient.invalidateQueries({ queryKey: getGetSiteQueryKey(siteId) });
          if (result.installStatus === 'verified') {
            toast({ title: 'Installation verified successfully' });
          } else {
            toast({ title: 'Widget not detected yet', description: 'Make sure the script is added to your site.' });
          }
        },
        onError: () => {
          toast({ title: 'Verification failed', variant: 'destructive' });
        },
      }
    );
  };

  const handleDelete = () => {
    if (!siteId) return;
    deleteSite.mutate(
      { siteId },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: getListSitesQueryKey() });
          toast({ title: 'Site deleted' });
          setLocation('/sites');
        },
        onError: () => {
          toast({ title: 'Failed to delete site', variant: 'destructive' });
        },
      }
    );
  };

  const handleRotateToken = () => {
    if (!siteId) return;
    rotateToken.mutate(
      { siteId },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: getGetSiteQueryKey(siteId) });
          toast({ title: 'Token rotated — update the script on your site' });
        },
        onError: () => {
          toast({ title: 'Failed to rotate token', variant: 'destructive' });
        },
      }
    );
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text).catch(() => {
      // Fallback for browsers that restrict clipboard access without https
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.focus();
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
    });
    toast({ title: 'Copied to clipboard' });
  };

  const embedSnippet = site
    ? `<script src="${window.location.origin}/api/public/widget.js" data-accessflow-token="${site.embedToken}" defer></script>`
    : '';

  const getStatusConfig = (status?: string) => {
    switch (status) {
      case 'verified':
        return { icon: CheckCircle2, color: 'text-chart-1', bgColor: 'bg-chart-1/10', label: 'Verified' };
      case 'pending':
        return { icon: Clock, color: 'text-chart-3', bgColor: 'bg-chart-3/10', label: 'Pending' };
      default:
        return { icon: AlertCircle, color: 'text-muted-foreground', bgColor: 'bg-muted', label: 'Not Installed' };
    }
  };

  const statusConfig = getStatusConfig(site?.installStatus);
  const StatusIcon = statusConfig.icon;

  if (siteLoading) {
    return (
      <AppShell>
        <div className="p-8 max-w-7xl mx-auto">
          <div className="animate-pulse space-y-8">
            <div className="h-8 bg-muted rounded w-1/3" />
            <div className="h-64 bg-muted rounded" />
          </div>
        </div>
      </AppShell>
    );
  }

  if (!site) {
    return (
      <AppShell>
        <div className="p-8 max-w-7xl mx-auto text-center">
          <p className="text-muted-foreground">Site not found</p>
          <Link href="/sites" className="text-primary hover:underline mt-4 inline-block">
            Back to sites
          </Link>
        </div>
      </AppShell>
    );
  }

  const CATEGORY_ORDER = ['screen-reader', 'keyboard', 'visual', 'structure', 'forms'] as const;
  const CATEGORY_LABELS: Record<string, string> = {
    'screen-reader': 'Screen Reader',
    keyboard: 'Keyboard Navigation',
    visual: 'Visual Accessibility',
    structure: 'Page Structure',
    forms: 'Forms & Inputs',
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'critical': return 'text-destructive bg-destructive/10';
      case 'serious': return 'text-chart-2 bg-chart-2/10';
      case 'moderate': return 'text-chart-3 bg-chart-3/10';
      case 'minor': return 'text-muted-foreground bg-muted';
      default: return 'text-muted-foreground bg-muted';
    }
  };

  return (
    <AppShell>
      <div className="p-4 md:p-8 max-w-7xl mx-auto">
        <div className="mb-8">
          <Link href="/sites" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-4" data-testid="link-back-to-sites">
            <ArrowLeft className="w-4 h-4" />
            Back to sites
          </Link>
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div className="min-w-0">
              <h1 className="font-sans font-bold tracking-tight text-3xl md:text-4xl text-foreground mb-1 truncate">{site.name}</h1>
              <p className="text-muted-foreground text-sm">{site.domain}</p>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <div className={`px-3 py-1.5 rounded-md ${statusConfig.bgColor} flex items-center gap-2`}>
                <StatusIcon className={`w-4 h-4 ${statusConfig.color}`} />
                <span className={`text-sm font-medium ${statusConfig.color}`}>{statusConfig.label}</span>
              </div>
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="outline" size="icon" data-testid="button-delete-site">
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Delete site?</AlertDialogTitle>
                    <AlertDialogDescription>
                      This will permanently delete {site.name} and all associated data. This action cannot be undone.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
                      Delete
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          </div>
        </div>

        <Tabs defaultValue={site.installStatus === 'verified' ? 'overview' : 'install'} className="space-y-6">
          <TabsList>
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="widget">Widget Configuration</TabsTrigger>
            <TabsTrigger value="install">Installation</TabsTrigger>
            <TabsTrigger value="scans">Scans</TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="p-6 rounded-lg border border-card-border bg-card shadow-[var(--shadow-card)]">
                <div className="text-sm text-muted-foreground mb-1">Monthly Visits</div>
                <div className="text-3xl font-semibold text-foreground">{site.monthlyVisits.toLocaleString()}</div>
              </div>
              <div className="p-6 rounded-lg border border-card-border bg-card shadow-[var(--shadow-card)]">
                <div className="text-sm text-muted-foreground mb-1">Latest Score</div>
                <div className="text-3xl font-semibold text-foreground">{site.latestScore ?? '—'}</div>
              </div>
              <div className="p-6 rounded-lg border border-card-border bg-card shadow-[var(--shadow-card)]">
                <div className="text-sm text-muted-foreground mb-1">Last Seen</div>
                <div className="text-lg font-medium text-foreground">
                  {site.lastSeenAt ? formatDistanceToNow(new Date(site.lastSeenAt), { addSuffix: true }) : 'Never'}
                </div>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="widget" className="space-y-6">
            {configLoading ? (
              <div className="p-6 rounded-lg border border-border bg-card shadow-[var(--shadow-card)] animate-pulse">
                <div className="h-96" />
              </div>
            ) : (
              <div className="p-6 rounded-lg border border-card-border bg-card shadow-[var(--shadow-card)] space-y-6">
                <div className="flex items-center gap-3 mb-6">
                  <SettingsIcon className="w-5 h-5 text-primary" />
                  <h2 className="text-xl font-semibold text-foreground">Widget Settings</h2>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <Label htmlFor="position">Position</Label>
                    <Select
                      value={currentConfig?.position}
                      onValueChange={(val) => handleConfigChange('position', val)}
                    >
                      <SelectTrigger id="position" data-testid="select-position">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="bottom-right">Bottom Right</SelectItem>
                        <SelectItem value="bottom-left">Bottom Left</SelectItem>
                        <SelectItem value="top-right">Top Right</SelectItem>
                        <SelectItem value="top-left">Top Left</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label htmlFor="accentColor">Accent Color</Label>
                    <div className="flex gap-2">
                      <Input
                        id="accentColor"
                        type="color"
                        value={currentConfig?.accentColor}
                        onChange={(e) => handleConfigChange('accentColor', e.target.value)}
                        className="w-16 h-10"
                        data-testid="input-accent-color"
                      />
                      <Input
                        value={currentConfig?.accentColor}
                        onChange={(e) => handleConfigChange('accentColor', e.target.value)}
                        placeholder="#000000"
                        className="flex-1"
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="launcherIcon">Launcher Icon</Label>
                    <Select
                      value={currentConfig?.launcherIcon}
                      onValueChange={(val) => handleConfigChange('launcherIcon', val)}
                    >
                      <SelectTrigger id="launcherIcon" data-testid="select-launcher-icon">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="accessibility">Accessibility</SelectItem>
                        <SelectItem value="universal">Universal</SelectItem>
                        <SelectItem value="eye">Eye</SelectItem>
                        <SelectItem value="hand">Hand</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label htmlFor="launcherSize">Launcher Size</Label>
                    <Select
                      value={currentConfig?.launcherSize}
                      onValueChange={(val) => handleConfigChange('launcherSize', val)}
                    >
                      <SelectTrigger id="launcherSize" data-testid="select-launcher-size">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="small">Small</SelectItem>
                        <SelectItem value="medium">Medium</SelectItem>
                        <SelectItem value="large">Large</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label htmlFor="language">Language</Label>
                    <Select
                      value={currentConfig?.language ?? 'en'}
                      onValueChange={(val) => handleConfigChange('language', val)}
                    >
                      <SelectTrigger id="language" data-testid="select-language">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="en">English (en)</SelectItem>
                        <SelectItem value="fr">French (fr)</SelectItem>
                        <SelectItem value="de">German (de)</SelectItem>
                        <SelectItem value="es">Spanish (es)</SelectItem>
                        <SelectItem value="pt">Portuguese (pt)</SelectItem>
                        <SelectItem value="it">Italian (it)</SelectItem>
                        <SelectItem value="nl">Dutch (nl)</SelectItem>
                        <SelectItem value="pl">Polish (pl)</SelectItem>
                        <SelectItem value="cs">Czech (cs)</SelectItem>
                        <SelectItem value="ro">Romanian (ro)</SelectItem>
                        <SelectItem value="sv">Swedish (sv)</SelectItem>
                        <SelectItem value="da">Danish (da)</SelectItem>
                        <SelectItem value="fi">Finnish (fi)</SelectItem>
                        <SelectItem value="no">Norwegian (no)</SelectItem>
                        <SelectItem value="hu">Hungarian (hu)</SelectItem>
                        <SelectItem value="el">Greek (el)</SelectItem>
                        <SelectItem value="ar">Arabic (ar)</SelectItem>
                        <SelectItem value="he">Hebrew (he)</SelectItem>
                        <SelectItem value="tr">Turkish (tr)</SelectItem>
                        <SelectItem value="ru">Russian (ru)</SelectItem>
                        <SelectItem value="ja">Japanese (ja)</SelectItem>
                        <SelectItem value="zh">Chinese Simplified (zh)</SelectItem>
                        <SelectItem value="ko">Korean (ko)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label htmlFor="brandingText">Branding Text (optional)</Label>
                    <Input
                      id="brandingText"
                      value={currentConfig?.brandingText || ''}
                      onChange={(e) => handleConfigChange('brandingText', e.target.value || null)}
                      placeholder="Powered by ADA Widget"
                      data-testid="input-branding-text"
                    />
                  </div>

                  <div className="flex items-center justify-between">
                    <Label htmlFor="hideOnMobile">Hide on Mobile</Label>
                    <Switch
                      id="hideOnMobile"
                      checked={currentConfig?.hideOnMobile}
                      onCheckedChange={(val) => handleConfigChange('hideOnMobile', val)}
                      data-testid="switch-hide-on-mobile"
                    />
                  </div>
                </div>

                {/* ── Accessibility Statement ── */}
                <div className="rounded-lg border border-card-border p-5 space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-foreground">Accessibility Statement</p>
                      <p className="text-sm text-muted-foreground mt-0.5">
                        Host a public statement documenting your ongoing work toward WCAG 2.1 AA.
                      </p>
                    </div>
                    <Switch
                      id="statementEnabled"
                      checked={currentConfig?.statementEnabled ?? false}
                      onCheckedChange={(val) => handleConfigChange('statementEnabled', val)}
                      data-testid="switch-statement-enabled"
                    />
                  </div>

                  {currentConfig?.statementEnabled && (
                    <div className="space-y-4 pt-2 border-t border-card-border">
                      <div>
                        <Label htmlFor="statementBusinessName">Business / Organisation Name</Label>
                        <Input
                          id="statementBusinessName"
                          value={currentConfig?.statementBusinessName || ''}
                          onChange={(e) => handleConfigChange('statementBusinessName', e.target.value || null)}
                          placeholder="Acme Corp"
                          data-testid="input-statement-business-name"
                        />
                      </div>
                      <div className="grid md:grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="statementContactEmail">Contact Email</Label>
                          <Input
                            id="statementContactEmail"
                            type="email"
                            value={currentConfig?.statementContactEmail || ''}
                            onChange={(e) => handleConfigChange('statementContactEmail', e.target.value || null)}
                            placeholder="accessibility@example.com"
                            data-testid="input-statement-contact-email"
                          />
                        </div>
                        <div>
                          <Label htmlFor="statementContactPhone">Contact Phone (optional)</Label>
                          <Input
                            id="statementContactPhone"
                            type="tel"
                            value={currentConfig?.statementContactPhone || ''}
                            onChange={(e) => handleConfigChange('statementContactPhone', e.target.value || null)}
                            placeholder="+1 555 000 0000"
                            data-testid="input-statement-contact-phone"
                          />
                        </div>
                      </div>
                      <div>
                        <Label htmlFor="additionalStatementText">Additional Statement Text (optional)</Label>
                        <Textarea
                          id="additionalStatementText"
                          value={currentConfig?.additionalStatementText || ''}
                          onChange={(e) => handleConfigChange('additionalStatementText', e.target.value || null)}
                          placeholder="Add known limitations, remediation timelines, or other context about your accessibility efforts."
                          className="mt-1.5 min-h-[120px]"
                          data-testid="textarea-additional-statement-text"
                        />
                        <p className="text-xs text-muted-foreground mt-1.5">
                          This text is appended after the standard WCAG 2.1 AA template. Paragraphs are separated by blank lines.
                        </p>
                      </div>

                      {site && (
                        <div>
                          <Label>Your Statement URL</Label>
                          <div className="flex items-center gap-2 mt-1.5">
                            <code className="flex-1 text-xs bg-muted rounded-md px-3 py-2 text-foreground break-all select-all" data-testid="text-statement-url">
                              {`${window.location.origin}/api/public/statement?token=${site.embedToken}`}
                            </code>
                            <Button
                              size="sm"
                              variant="outline"
                              type="button"
                              onClick={() => {
                                navigator.clipboard.writeText(
                                  `${window.location.origin}/api/public/statement?token=${site.embedToken}`
                                );
                                toast({ title: 'Statement URL copied' });
                              }}
                            >
                              Copy
                            </Button>
                          </div>
                          <p className="text-xs text-muted-foreground mt-1.5">
                            This link will appear automatically in the widget footer when visitors open the accessibility panel.
                          </p>
                        </div>
                      )}
                    </div>
                  )}

                  {!currentConfig?.statementEnabled && (
                    <div className="pt-2 border-t border-card-border">
                      <Label htmlFor="statementUrl">Custom Statement URL (optional)</Label>
                      <Input
                        id="statementUrl"
                        value={currentConfig?.statementUrl || ''}
                        onChange={(e) => handleConfigChange('statementUrl', e.target.value || null)}
                        placeholder="https://example.com/accessibility"
                        className="mt-1.5"
                        data-testid="input-statement-url"
                      />
                      <p className="text-xs text-muted-foreground mt-1.5">
                        Link to an existing statement page on your own site. Enable the toggle above to host one here instead.
                      </p>
                    </div>
                  )}
                </div>

                <div>
                  <Label className="mb-3 block">Enabled Features</Label>
                  <div className="grid md:grid-cols-2 gap-3">
                    {ALL_FEATURES.map((feature) => (
                      <div key={feature.id} className="flex items-center justify-between p-3 rounded-md border border-card-border bg-muted/30">
                        <span className="text-sm text-foreground">{feature.label}</span>
                        <Switch
                          checked={currentConfig?.enabledFeatures?.includes(feature.id)}
                          onCheckedChange={(checked) => {
                            const current = currentConfig?.enabledFeatures || [];
                            handleConfigChange(
                              'enabledFeatures',
                              checked ? [...current, feature.id] : current.filter((f: string) => f !== feature.id)
                            );
                          }}
                          data-testid={`switch-feature-${feature.id}`}
                        />
                      </div>
                    ))}
                  </div>
                </div>

                <div className="flex justify-end pt-4 border-t border-card-border">
                  <Button
                    onClick={handleSaveConfig}
                    disabled={updateConfig.isPending || !localConfig}
                    data-testid="button-save-config"
                  >
                    {updateConfig.isPending ? 'Saving...' : 'Save Configuration'}
                  </Button>
                </div>
              </div>
            )}
          </TabsContent>

          <TabsContent value="install" className="space-y-6">
            {site.installStatus === 'verified' ? (
              <div className="space-y-8">
                <div className="p-8 rounded-xl border border-chart-1/30 bg-chart-1/10 flex flex-col sm:flex-row items-center gap-6 shadow-sm">
                  <div className="w-16 h-16 shrink-0 bg-chart-1/20 text-chart-1 rounded-full flex items-center justify-center">
                    <CheckCircle2 className="w-8 h-8" />
                  </div>
                  <div className="text-center sm:text-left">
                    <h2 className="text-2xl font-bold text-foreground mb-2">Your site is connected</h2>
                    <p className="text-muted-foreground">
                      The ADA Widget is successfully installed on <strong>{site.domain}</strong> and is actively providing accessibility features to your visitors.
                    </p>
                  </div>
                </div>

                <div className="border border-card-border rounded-xl bg-card shadow-sm overflow-hidden">
                  <div className="p-6 border-b border-card-border bg-muted/30">
                    <h3 className="text-lg font-semibold text-foreground mb-1">Installation details</h3>
                    <p className="text-sm text-muted-foreground">
                      Your widget is live, but you can reference your installation settings and code here if you ever change website platforms.
                    </p>
                  </div>
                  <div className="p-6 md:p-8">
                    <InstallationWizard
                      site={site}
                      embedSnippet={embedSnippet}
                      onCopy={copyToClipboard}
                      onVerify={handleVerify}
                      verifyPending={verifySite.isPending}
                      onRotate={handleRotateToken}
                      rotatePending={rotateToken.isPending}
                    />
                  </div>
                </div>
              </div>
            ) : (
              <div className="space-y-8">
                <div className="p-8 rounded-xl border border-primary/20 bg-primary/5 flex flex-col items-center text-center shadow-sm">
                  <h2 className="text-2xl font-bold text-foreground mb-2">Let's get your widget online</h2>
                  <p className="text-muted-foreground max-w-xl mx-auto">
                    Follow the steps below to add the ADA Widget to <strong>{site.domain}</strong>. You do not need to be a developer to do this.
                  </p>
                </div>

                <InstallationWizard
                  site={site}
                  embedSnippet={embedSnippet}
                  onCopy={copyToClipboard}
                  onVerify={handleVerify}
                  verifyPending={verifySite.isPending}
                  onRotate={handleRotateToken}
                  rotatePending={rotateToken.isPending}
                />
              </div>
            )}
          </TabsContent>

          <TabsContent value="scans" className="space-y-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold text-foreground flex items-center gap-3">
                <FileSearch className="w-5 h-5 text-primary" />
                Accessibility Scans
              </h2>
              <Button onClick={handleRunScan} disabled={runScan.isPending} data-testid="button-run-scan">
                <Play className="w-4 h-4 mr-2" />
                {runScan.isPending ? 'Starting...' : 'Run New Scan'}
              </Button>
            </div>

            {scansLoading ? (
              <div className="p-6 rounded-lg border border-border bg-card animate-pulse">
                <div className="h-64" />
              </div>
            ) : !scans || scans.length === 0 ? (
              <div className="p-12 rounded-lg border border-card-border bg-card text-center">
                <FileSearch className="w-12 h-12 text-muted-foreground/40 mx-auto mb-4" />
                <p className="text-muted-foreground mb-4">No scans yet</p>
                <Button onClick={handleRunScan} disabled={runScan.isPending}>
                  <Play className="w-4 h-4 mr-2" />
                  Run Your First Scan
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                {scans.map((scan) => (
                  <div key={scan.id} className="p-6 rounded-lg border border-card-border bg-card" data-testid={`scan-${scan.id}`}>
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <div className="flex items-center gap-3 mb-2">
                          <span className="text-2xl font-semibold text-foreground">
                            {scan.score !== null ? `${scan.score}/100` : '—'}
                          </span>
                          {scan.status === 'completed' ? (
                            <span className="px-2.5 py-1 rounded-md bg-chart-1/10 text-chart-1 text-xs font-medium">
                              Completed
                            </span>
                          ) : (
                            <span className="px-2.5 py-1 rounded-md bg-destructive/10 text-destructive text-xs font-medium">
                              Failed
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-muted-foreground">
                          {formatDistanceToNow(new Date(scan.createdAt), { addSuffix: true })}
                        </p>
                      </div>
                      <div className="flex items-start gap-4">
                        <div className="flex gap-2">
                          <div className="text-center">
                            <div className={`text-lg font-semibold ${scan.issueCounts.critical > 0 ? 'text-destructive' : 'text-muted-foreground'}`}>
                              {scan.issueCounts.critical}
                            </div>
                            <div className="text-xs text-muted-foreground">Critical</div>
                          </div>
                          <div className="text-center">
                            <div className={`text-lg font-semibold ${scan.issueCounts.serious > 0 ? 'text-chart-2' : 'text-muted-foreground'}`}>
                              {scan.issueCounts.serious}
                            </div>
                            <div className="text-xs text-muted-foreground">Serious</div>
                          </div>
                          <div className="text-center">
                            <div className={`text-lg font-semibold ${scan.issueCounts.moderate > 0 ? 'text-chart-3' : 'text-muted-foreground'}`}>
                              {scan.issueCounts.moderate}
                            </div>
                            <div className="text-xs text-muted-foreground">Moderate</div>
                          </div>
                          <div className="text-center">
                            <div className="text-lg font-semibold text-muted-foreground">{scan.issueCounts.minor}</div>
                            <div className="text-xs text-muted-foreground">Minor</div>
                          </div>
                        </div>
                        <a
                          href={`/sites/${siteId}/report/${scan.id}`}
                          target="_blank"
                          rel="noreferrer"
                          data-testid={`button-download-report-${scan.id}`}
                        >
                          <Button variant="outline" size="sm" className="gap-1.5 whitespace-nowrap">
                            <Download className="w-3.5 h-3.5" />
                            Download report
                          </Button>
                        </a>
                      </div>
                    </div>

                    {scan.errorMessage && (
                      <div className="p-3 rounded-md bg-destructive/10 text-destructive text-sm mb-4">
                        {scan.errorMessage}
                      </div>
                    )}

                    {scan.findings && scan.findings.length > 0 && (
                      <div className="space-y-5">
                        {CATEGORY_ORDER.map((cat) => {
                          const items = scan.findings!.filter(
                            (f) => (f.category ?? 'structure') === cat
                          );
                          if (items.length === 0) return null;
                          return (
                            <div key={cat}>
                              <h4 className="text-sm font-semibold text-foreground mb-2 flex items-center gap-2">
                                {CATEGORY_LABELS[cat]}
                                <span className="text-xs font-normal text-muted-foreground">
                                  {items.length} {items.length === 1 ? 'issue' : 'issues'}
                                </span>
                              </h4>
                              <div className="space-y-2">
                                {items.map((finding, i) => (
                                  <div key={i} className="p-3 rounded-md border border-card-border bg-muted/30 text-sm">
                                    <div className="flex flex-wrap items-start justify-between gap-2 mb-1">
                                      <span className="font-medium text-foreground">{finding.message}</span>
                                      <div className="flex items-center gap-2 flex-shrink-0">
                                        {finding.wcag && (
                                          <span className="px-2 py-0.5 rounded text-xs font-medium bg-muted text-muted-foreground">
                                            WCAG {finding.wcag}
                                          </span>
                                        )}
                                        <span className={`px-2 py-0.5 rounded text-xs font-medium ${getSeverityColor(finding.severity)}`}>
                                          {finding.severity}
                                        </span>
                                      </div>
                                    </div>
                                    {finding.howToFix && (
                                      <p className="text-muted-foreground text-xs leading-relaxed mb-1.5">
                                        <span className="font-medium text-foreground/80">How to fix: </span>
                                        {finding.howToFix}
                                      </p>
                                    )}
                                    <p className="text-xs text-muted-foreground">
                                      {finding.count} {finding.count === 1 ? 'instance' : 'instances'}
                                      {finding.sample && (
                                        <span className="ml-2 font-mono text-muted-foreground/70 break-all">
                                          {finding.sample}
                                        </span>
                                      )}
                                    </p>
                                  </div>
                                ))}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </AppShell>
  );
}
